import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Pagination } from "@/components/ui/pagination";
import {
  Plus,
  Edit2,
  Trash2,
  Building,
  Eye,
  EyeOff,
  Shield,
  LayoutDashboard,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { BRANCHES_ACTIVE_QUERY_KEY } from "@/hooks/useActiveBranches";
import { cn } from "@/lib/utils";

const BranchesSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBlockers, setDeleteBlockers] = useState<string[]>([]);

  useEffect(() => {
    if (isTenantAdmin) {
      loadBranches();
    }
  }, [isTenantAdmin, includeInactive]);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const data = await branchesApi.getAll(includeInactive);
      setBranches(data);
    } catch (error) {
      console.error("Failed to load branches:", error);
      toast.error("Unable to load branches. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch: Branch) => {
    navigate(`/setup/branches/${branch.id}/edit`);
  };

  const handleManagePermissions = (branch: Branch) => {
    navigate(`/setup/branches/${branch.id}/permissions`);
  };

  const openDeleteDialog = (branch: Branch) => {
    setBranchToDelete(branch);
    setDeleteBlockers([]);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!branchToDelete) return;
    setDeleting(true);
    setDeleteBlockers([]);
    try {
      const result = await branchesApi.delete(branchToDelete.id);
      if (result.deleted) {
        toast.success(`"${branchToDelete.name}" has been permanently deleted.`);
      } else {
        toast.success(
          `"${branchToDelete.name}" has been deactivated. It will no longer appear in circulation workflows.`
        );
        // Show inactive branches so user can see the deactivated branch
        setIncludeInactive(true);
      }
      queryClient.invalidateQueries(BRANCHES_ACTIVE_QUERY_KEY);
      setDeleteDialogOpen(false);
      setBranchToDelete(null);
      await loadBranches();
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.blockers && Array.isArray(data.blockers)) {
        // Backend returned blocking records — show them in the dialog
        setDeleteBlockers(data.blockers);
      } else {
        toast.error(data?.message || "Unable to process request. Please try again.");
        setDeleteDialogOpen(false);
        setBranchToDelete(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleReactivate = async (branch: Branch) => {
    try {
      await branchesApi.reactivate(branch.id);
      toast.success(`"${branch.name}" has been reactivated.`);
      queryClient.invalidateQueries(BRANCHES_ACTIVE_QUERY_KEY);
      await loadBranches();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to reactivate branch.");
    }
  };

  const filteredBranches = useMemo(() => {
    return branches.filter(
      (branch) =>
        branch.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        branch.code.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [branches, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeInactive]);

  // Paginate the filtered results
  const paginatedBranches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredBranches.slice(startIndex, endIndex);
  }, [filteredBranches, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage branches.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Branches"
          description="Manage library branches and their permissions"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Branches" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search branches..."
        />

        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive branches
            </Label>
          </div>
          <Button onClick={() => navigate("/setup/branches/new")} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Branch
          </Button>
        </div>

        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading branches..." />
            </div>
          ) : filteredBranches.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Building className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching branches" : "No branches yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term."
                  : "Create your first branch to organize your library locations."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/setup/branches/new")} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Branch
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {paginatedBranches.map((branch) => (
                  <div
                    key={branch.id}
                    className={cn(
                      "p-5 hover:bg-gray-50 transition-colors",
                      branch.status !== "ACTIVE" && "bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{branch.name}</h3>
                          {branch.isMain && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              Main Branch
                            </span>
                          )}
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded-full text-xs font-medium",
                              branch.status === "ACTIVE"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            )}
                          >
                            {branch.status === "ACTIVE" ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <span>Code: {branch.code}</span>
                          {branch._count && branch._count.staff > 0 && (
                            <span>
                              {branch._count.staff} staff member
                              {branch._count.staff !== 1 ? "s" : ""}
                            </span>
                          )}
                          {branch.permissions && branch.permissions.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Shield className="w-3.5 h-3.5" />
                              {branch.permissions.length} permission
                              {branch.permissions.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {branch.status === "ACTIVE" ? (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => navigate(`/setup/branches/${branch.id}`)}
                              className="gap-1.5"
                            >
                              <LayoutDashboard className="w-4 h-4" />
                              <span className="hidden sm:inline">Dashboard</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleManagePermissions(branch)}
                              className="gap-1.5"
                            >
                              <Shield className="w-4 h-4" />
                              <span className="hidden sm:inline">Permissions</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(branch)}
                              className="gap-1.5"
                            >
                              <Edit2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                            {/* "Deactivate" for active branches */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(branch)}
                              className="gap-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 hover:border-amber-200"
                            >
                              <EyeOff className="w-4 h-4" />
                              <span className="hidden sm:inline">Deactivate</span>
                            </Button>
                          </>
                        ) : (
                          <>
                            {/* "Reactivate" for inactive branches */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReactivate(branch)}
                              className="gap-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 hover:border-green-200"
                            >
                              <RefreshCw className="w-4 h-4" />
                              <span className="hidden sm:inline">Reactivate</span>
                            </Button>
                            {/* "Delete Permanently" only for inactive branches */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(branch)}
                              className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Delete</span>
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredBranches.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredBranches.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </PageContainer>

      {/* Two-stage lifecycle dialog */}
      {deleteDialogOpen && branchToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
            {/* Header */}
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                {branchToDelete.status === "ACTIVE" ? (
                  <>
                    <EyeOff className="h-5 w-5 text-amber-500" />
                    Deactivate Branch
                  </>
                ) : deleteBlockers.length > 0 ? (
                  <>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Cannot Delete
                  </>
                ) : (
                  <>
                    <Trash2 className="h-5 w-5 text-red-500" />
                    Permanently Delete Branch
                  </>
                )}
              </h2>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              {branchToDelete.status === "ACTIVE" ? (
                // Stage 1: Deactivate
                <div className="space-y-3">
                  <p className="text-sm">
                    This will deactivate <strong>{branchToDelete.name}</strong>. The branch will be
                    hidden from circulation workflows but all existing records (items, checkouts,
                    patron data) will be preserved.
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <p className="font-medium mb-1">After deactivating:</p>
                    <ul className="list-disc pl-4 space-y-1 text-amber-700">
                      <li>The branch won't appear in new circulation or registration forms</li>
                      <li>All historical data is preserved</li>
                      <li>You can reactivate it at any time</li>
                      <li>If the branch has no records, you can then permanently delete it</li>
                    </ul>
                  </div>
                </div>
              ) : deleteBlockers.length > 0 ? (
                // Blocked: show blockers
                <div className="space-y-3">
                  <p className="text-sm">
                    <strong>{branchToDelete.name}</strong> cannot be permanently deleted because it
                    still has associated records in the system. These records must be handled first
                    to maintain data integrity.
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800 mb-2 flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4" />
                      Blocking records:
                    </p>
                    <ul className="space-y-1">
                      {deleteBlockers.map((b, i) => (
                        <li key={i} className="text-sm text-red-700 flex items-start gap-1.5">
                          <span className="mt-0.5 text-red-400">•</span>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This is intentional — library systems preserve all historical records even when
                    a branch closes, so circulation history and patron registrations are never lost.
                  </p>
                </div>
              ) : (
                // Stage 2: Permanent delete
                <div className="space-y-3">
                  <p className="text-sm">
                    This will <strong className="text-red-600">permanently delete</strong> the
                    branch <strong>{branchToDelete.name}</strong>. This action cannot be undone.
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                    This branch has no associated records and can be safely removed.
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-2 p-4 border-t justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setBranchToDelete(null);
                  setDeleteBlockers([]);
                }}
              >
                {deleteBlockers.length > 0 ? "Close" : "Cancel"}
              </Button>
              {deleteBlockers.length === 0 && (
                <Button
                  variant={branchToDelete.status === "ACTIVE" ? "default" : "destructive"}
                  onClick={handleDelete}
                  disabled={deleting}
                  className={
                    branchToDelete.status === "ACTIVE" ? "bg-amber-500 hover:bg-amber-600" : ""
                  }
                >
                  {deleting
                    ? "Processing..."
                    : branchToDelete.status === "ACTIVE"
                      ? "Deactivate"
                      : "Delete Permanently"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default BranchesSettingsPage;
